// ADR CTV-732: pure helpers for the super-admin Publica upload
// worklist. Kept out of the page component so the react-refresh lint rule
// (component files export only components) stays satisfied and so the grouping
// rules are unit-testable without rendering.

import type { PublicaUploadRow } from '../../services/publicaUploadsService';

export interface SubmissionGroup {
  submissionId: string;
  campaignName: string | null;
  advertiserName: string | null;
  startDate: string | null;
  endDate: string | null;
  submissionStatus: string;
  /** For deep-linking the publisher-side submission detail page. */
  stationSlug: string | null;
  /** Captured: this submission's push is waiting on the files below. */
  blocking: boolean;
  rows: PublicaUploadRow[];
}

export interface StationGroup {
  stationId: string;
  stationName: string;
  submissions: SubmissionGroup[];
  /** Files waiting across every submission on this station. */
  count: number;
}

export interface OrgGroup {
  organizationId: string;
  organizationName: string;
  /** Null when the org has no publica_publisher_id — no deep link possible. */
  publicaPublisherId: string | null;
  stations: StationGroup[];
  /** Total rows across every station in this org. */
  count: number;
  /** How many of those are paid campaigns waiting to push. */
  blockingCount: number;
}

/**
 * Group the flat worklist org -> station -> submission, preserving the
 * server's ordering.
 *
 * The submission tier matters because that is the unit an admin actually
 * works: one campaign's files go into the library together, and the later
 * gate opens per submission, not per file. It also gives each group a place to
 * carry campaign context (advertiser, flight window, whether it is blocking)
 * that would otherwise be repeated on every row.
 *
 * The server already sorts (blocking first, then org, station, submission age,
 * creative age), so this walks in order and never re-sorts: each group's
 * position is the position of its first row, which is what puts the org
 * holding a blocking task at the top of the page.
 */
export function groupUploads(rows: PublicaUploadRow[]): OrgGroup[] {
  const orgs: OrgGroup[] = [];
  const orgIndex = new Map<string, OrgGroup>();
  const stationIndex = new Map<string, StationGroup>();
  const submissionIndex = new Map<string, SubmissionGroup>();

  for (const row of rows) {
    const orgId = row.organizationId ?? 'unknown-org';
    let org = orgIndex.get(orgId);
    if (!org) {
      org = {
        organizationId: orgId,
        organizationName: row.organizationName ?? 'Unknown organization',
        publicaPublisherId: row.publicaPublisherId,
        stations: [],
        count: 0,
        blockingCount: 0,
      };
      orgIndex.set(orgId, org);
      orgs.push(org);
    }

    const stationId = row.stationId ?? 'unknown-station';
    const stationKey = `${orgId}::${stationId}`;
    let station = stationIndex.get(stationKey);
    if (!station) {
      station = {
        stationId,
        stationName: row.stationName ?? 'Unknown channel',
        submissions: [],
        count: 0,
      };
      stationIndex.set(stationKey, station);
      org.stations.push(station);
    }

    // Keyed by station too: the same submission id can never span stations,
    // but keying defensively keeps a malformed row from merging groups.
    const submissionKey = `${stationKey}::${row.submissionId}`;
    let submission = submissionIndex.get(submissionKey);
    if (!submission) {
      submission = {
        submissionId: row.submissionId,
        campaignName: row.campaignName,
        advertiserName: row.advertiserName,
        startDate: row.startDate,
        endDate: row.endDate,
        submissionStatus: row.submissionStatus,
        stationSlug: row.stationSlug,
        blocking: row.blocking,
        rows: [],
      };
      submissionIndex.set(submissionKey, submission);
      station.submissions.push(submission);
    }

    submission.rows.push(row);
    station.count += 1;
    org.count += 1;
    if (row.blocking) org.blockingCount += 1;
  }

  return orgs;
}

/**
 * Deep link into a station's Publica creative library. Null when we don't know
 * the org's publisher id, in which case the page shows no link rather than a
 * broken one.
 */
export function publicaLibraryUrl(publicaPublisherId: string | null): string | null {
  if (!publicaPublisherId) return null;
  return `https://app.getpublica.com/#/app/pub/${publicaPublisherId}/creative-library`;
}

export type UploadPillKind = 'blocking' | 'failedVerification';

/** Tailwind classes for the status chips. */
export function uploadPillClass(kind: UploadPillKind): string {
  switch (kind) {
    case 'blocking':
      return 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-200';
    case 'failedVerification':
    default:
      return 'bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200';
  }
}

/**
 * Chips for ONE creative row. Submission-level state (blocking vs still in
 * review) is rendered once on the submission header instead, so the only
 * per-row concern left is whether the library scrape disagreed with an
 * admin's "uploaded" claim.
 */
export function uploadRowPills(row: PublicaUploadRow): UploadPillKind[] {
  return row.rowKind === 'miss' ? ['failedVerification'] : [];
}

/** Campaign window as a compact "Jun 1 – Jun 30" label. Empty when unknown. */
export function formatFlightWindow(
  startDate: string | null,
  endDate: string | null,
): string {
  const fmt = (d: string | null): string | null => {
    if (!d) return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
    if (!m) return d;
    const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(date);
  };
  const s = fmt(startDate);
  const e = fmt(endDate);
  if (s && e) return `${s} – ${e}`;
  return s ?? e ?? '';
}
